/**
 * @module components/cases
 *
 * The proof section: case studies presented as a tab set — one tab per client,
 * one panel with the situation / intervention / outcome story, the hard numbers,
 * a quote and the ADM phases and capabilities that were in play.
 *
 * Implemented as a proper WAI-ARIA tab set: `role="tablist"`, `role="tab"`,
 * `role="tabpanel"`, roving `tabindex`, arrow-key navigation.
 */

import { gsap, EASE, MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs, qsa, on, setAttributes } from "../core/dom.js";
import { CASES } from "../data/cases.js";
import { getPhaseById } from "../data/phases.js";
import { getCapabilityById } from "../data/capabilities.js";
import { icon } from "../svg/icons.js";

/**
 * Render the panel for one case study.
 *
 * @param {import("../data/cases.js").CaseStudy} entry
 * @returns {string} Markup.
 */
function panelMarkup(entry) {
  const metrics = entry.metrics
    .map(
      (metric) => `
      <div class="case-metric">
        <p class="case-metric__value">${metric.value}</p>
        <p class="case-metric__label">${metric.label}</p>
        ${metric.detail ? `<p class="case-metric__detail">${metric.detail}</p>` : ""}
      </div>`
    )
    .join("");

  const phasePills = entry.phases
    .map((id) => getPhaseById(id))
    .filter(Boolean)
    .map((phase) => `<li class="pill">${phase.code} · ${phase.name}</li>`)
    .join("");

  const capabilityPills = entry.capabilities
    .map((id) => getCapabilityById(id))
    .filter(Boolean)
    .map((capability) => `<li class="pill pill--solid">${capability.name}</li>`)
    .join("");

  return `
    <div class="case-panel__story">
      <div class="case-panel__meta">
        <span>${entry.sector}</span>
        <span>${entry.size}</span>
        <span>${entry.duration} months</span>
      </div>
      <h3 class="case-panel__title">${entry.title}</h3>
      <p class="case-panel__summary">${entry.summary}</p>

      <div class="case-beats">
        <div class="case-beat">
          <p class="case-beat__label">Situation</p>
          <p class="case-beat__body">${entry.situation}</p>
        </div>
        <div class="case-beat">
          <p class="case-beat__label">Intervention</p>
          <p class="case-beat__body">${entry.intervention}</p>
        </div>
        <div class="case-beat">
          <p class="case-beat__label">Outcome</p>
          <p class="case-beat__body">${entry.outcome}</p>
        </div>
      </div>
    </div>

    <aside class="case-panel__aside">
      <dl class="case-metrics">${metrics}</dl>

      <blockquote class="case-quote">
        ${icon("quote", { size: 18 })}
        ${entry.quote}
        <span class="case-quote__attribution">${entry.attribution}</span>
      </blockquote>

      <ul class="case-tags" aria-label="ADM phases exercised">${phasePills}</ul>
      <ul class="case-tags" aria-label="Capabilities applied">${capabilityPills}</ul>
    </aside>`;
}

/**
 * @returns {() => void} Teardown.
 */
export function initCases() {
  const root = qs("[data-cases]");
  if (!root) return () => {};

  const tabList = qs("[data-case-tabs]", root);
  const panelArea = qs("[data-case-panel]", root);
  if (!tabList || !panelArea) return () => {};

  tabList.setAttribute("role", "tablist");
  tabList.setAttribute("aria-label", "Case studies");

  tabList.innerHTML = CASES.map(
    (entry, index) => `
    <button type="button" class="case-tab" role="tab" id="case-tab-${entry.id}"
            data-case-tab="${entry.id}" aria-controls="case-panel-region"
            aria-selected="${index === 0}" tabindex="${index === 0 ? "0" : "-1"}"
            style="--case-accent:${entry.accent}">
      <span class="case-tab__sector">${entry.sector}</span>
      <span class="case-tab__client">${entry.client}</span>
    </button>`
  ).join("");

  panelArea.setAttribute("role", "tabpanel");
  panelArea.setAttribute("id", "case-panel-region");
  panelArea.setAttribute("aria-labelledby", `case-tab-${CASES[0].id}`);

  const tabs = qsa("[data-case-tab]", tabList);

  const select = (id, { focus = false } = {}) => {
    const entry = CASES.find((item) => item.id === id);
    if (!entry) return;

    tabs.forEach((tab) => {
      const selected = tab.getAttribute("data-case-tab") === id;
      setAttributes(tab, { "aria-selected": String(selected), tabindex: selected ? "0" : "-1" });
      if (selected && focus) tab.focus();
    });

    panelArea.setAttribute("aria-labelledby", `case-tab-${id}`);
    panelArea.style.setProperty("--case-accent", entry.accent);
    panelArea.innerHTML = panelMarkup(entry);

    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.fromTo(
        panelArea.querySelectorAll(".case-beat, .case-metric, .case-quote, .case-tags li"),
        { autoAlpha: 0, y: 12 },
        { autoAlpha: 1, y: 0, duration: 0.45, ease: EASE.out, stagger: 0.025, overwrite: true }
      );
    }
  };

  const disposers = [];
  tabs.forEach((tab, index) => {
    disposers.push(on(tab, "click", () => select(tab.getAttribute("data-case-tab"))));
    disposers.push(
      on(tab, "keydown", (event) => {
        let next = null;
        if (event.key === "ArrowRight") next = (index + 1) % CASES.length;
        else if (event.key === "ArrowLeft") next = (index - 1 + CASES.length) % CASES.length;
        else if (event.key === "Home") next = 0;
        else if (event.key === "End") next = CASES.length - 1;
        if (next === null) return;
        event.preventDefault();
        select(CASES[next].id, { focus: true });
      })
    );
  });

  select(CASES[0].id);

  /* --- Motion ------------------------------------------------------------ */

  const mm = createMotionContext();
  mm.add(MEDIA.reduce, () => {});

  return () => {
    disposers.forEach((dispose) => dispose());
    mm.revert();
  };
}
